// Modelo de formulário de "Meu cadastro" (`/me/person`; Parte 6.5/6.7; REQ-PPL-007, 012, 016; GIY-067): contatos, disponibilidade
// declarada e ausências da PRÓPRIA pessoa. Esquemas zod com mensagens que são chaves de `people.json` (`me.forms.errors.*`) e
// conversões para o contrato. As regras de negócio (sobreposição, vínculo vigente, contato duplicado…) são do servidor: aqui só
// se evita enviar o que ele certamente recusa. Sem React, testável.
import { z } from 'zod';

import {
  ABSENCE_KINDS,
  AVAILABILITY_KINDS,
  CONTACT_KINDS,
  WEEK_DAYS,
  type AbsenceInput,
  type Availability,
  type AvailabilityInput,
  type ContactInput,
  type PersonContact,
  type TimeWindowInput,
  type WeekDay,
} from '../api/queries';
import { isEmail, isIsoDate } from './validation';

const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

export function isTime(value: string): boolean {
  return TIME_PATTERN.test(value);
}

/** Minutos desde a meia-noite de um `HH:mm` válido. */
export function minutesOf(value: string): number {
  const [hours = '0', minutes = '0'] = value.split(':');
  return Number(hours) * 60 + Number(minutes);
}

/** `HH:mm` (a API pode devolver `HH:mm:ss`). */
export function shortTime(value: string): string {
  return value.slice(0, 5);
}

// ---- Contatos ----

const contactSchema = z.object({
  kind: z.enum(CONTACT_KINDS),
  value: z.string().trim().min(1, 'me.forms.errors.required').max(254, 'me.forms.errors.tooLong'),
  isPrimary: z.boolean(),
});

export const contactsSchema = z
  .object({ contacts: z.array(contactSchema).max(20, 'me.forms.errors.tooMany') })
  .superRefine((form, context) => {
    const primaries = new Set<string>();
    form.contacts.forEach((contact, index) => {
      if (
        contact.kind === 'Email' &&
        contact.value.trim() !== '' &&
        !isEmail(contact.value.trim())
      ) {
        context.addIssue({
          code: 'custom',
          path: ['contacts', index, 'value'],
          message: 'me.forms.errors.email',
        });
      }
      if (contact.isPrimary) {
        if (primaries.has(contact.kind)) {
          context.addIssue({
            code: 'custom',
            path: ['contacts', index, 'isPrimary'],
            message: 'me.forms.errors.onePrimary',
          });
        }
        primaries.add(contact.kind);
      }
    });
  });

export type ContactsFormValues = z.infer<typeof contactsSchema>;

export function contactsToForm(contacts: readonly PersonContact[]): ContactsFormValues {
  return {
    contacts: contacts.flatMap((contact) =>
      (CONTACT_KINDS as readonly string[]).includes(contact.kind)
        ? [
            {
              kind: contact.kind as ContactsFormValues['contacts'][number]['kind'],
              value: contact.value,
              isPrimary: contact.isPrimary,
            },
          ]
        : [],
    ),
  };
}

export function toContactInputs(values: ContactsFormValues): ContactInput[] {
  return values.contacts.map((contact) => ({
    kind: contact.kind,
    value: contact.value.trim(),
    isPrimary: contact.isPrimary,
  }));
}

// ---- Disponibilidade ----

const windowSchema = z.object({ start: z.string(), end: z.string() });
const windowsSchema = z.array(windowSchema);

export const availabilitySchema = z
  .object({
    kind: z.enum(AVAILABILITY_KINDS),
    weekly: z.object({
      monday: windowsSchema,
      tuesday: windowsSchema,
      wednesday: windowsSchema,
      thursday: windowsSchema,
      friday: windowsSchema,
      saturday: windowsSchema,
      sunday: windowsSchema,
    }),
    date: z.string(),
    startTime: z.string(),
    endTime: z.string(),
  })
  .superRefine((form, context) => {
    if (form.kind === 'Weekly') {
      let total = 0;
      for (const day of WEEK_DAYS) {
        const windows = form.weekly[day];
        total += windows.length;
        windows.forEach((window, index) => {
          if (!isTime(window.start)) {
            context.addIssue({
              code: 'custom',
              path: ['weekly', day, index, 'start'],
              message: 'me.forms.errors.time',
            });
          }
          if (!isTime(window.end)) {
            context.addIssue({
              code: 'custom',
              path: ['weekly', day, index, 'end'],
              message: 'me.forms.errors.time',
            });
          }
          if (
            isTime(window.start) &&
            isTime(window.end) &&
            minutesOf(window.start) >= minutesOf(window.end)
          ) {
            context.addIssue({
              code: 'custom',
              path: ['weekly', day, index, 'end'],
              message: 'me.forms.errors.startBeforeEnd',
            });
          }
        });
        const sorted = windows
          .filter((window) => isTime(window.start) && isTime(window.end))
          .sort((a, b) => minutesOf(a.start) - minutesOf(b.start));
        sorted.forEach((window, index) => {
          const previous = sorted[index - 1];
          if (previous && minutesOf(window.start) < minutesOf(previous.end)) {
            context.addIssue({
              code: 'custom',
              path: ['weekly', day],
              message: 'me.forms.errors.overlap',
            });
          }
        });
      }
      if (total === 0) {
        context.addIssue({
          code: 'custom',
          path: ['weekly'],
          message: 'me.forms.errors.noWindows',
        });
      }
      return;
    }
    if (!isIsoDate(form.date)) {
      context.addIssue({ code: 'custom', path: ['date'], message: 'me.forms.errors.date' });
    }
    if (!isTime(form.startTime)) {
      context.addIssue({ code: 'custom', path: ['startTime'], message: 'me.forms.errors.time' });
    }
    if (!isTime(form.endTime)) {
      context.addIssue({ code: 'custom', path: ['endTime'], message: 'me.forms.errors.time' });
    }
    if (
      isTime(form.startTime) &&
      isTime(form.endTime) &&
      minutesOf(form.startTime) >= minutesOf(form.endTime)
    ) {
      context.addIssue({
        code: 'custom',
        path: ['endTime'],
        message: 'me.forms.errors.startBeforeEnd',
      });
    }
  });

export type AvailabilityFormValues = z.infer<typeof availabilitySchema>;

export function emptyWeekly(): AvailabilityFormValues['weekly'] {
  return {
    monday: [],
    tuesday: [],
    wednesday: [],
    thursday: [],
    friday: [],
    saturday: [],
    sunday: [],
  };
}

export function emptyAvailabilityForm(): AvailabilityFormValues {
  return { kind: 'Weekly', weekly: emptyWeekly(), date: '', startTime: '', endTime: '' };
}

/** Declaração existente → valores do formulário (edição). */
export function availabilityToForm(item: Availability): AvailabilityFormValues {
  const weekly = emptyWeekly();
  for (const day of WEEK_DAYS) {
    weekly[day] = (item.weekly?.[day] ?? []).map((window) => ({
      start: shortTime(window.start),
      end: shortTime(window.end),
    }));
  }
  return {
    kind: item.kind === 'Dated' ? 'Dated' : 'Weekly',
    weekly,
    date: item.date ?? '',
    startTime: item.startTime ? shortTime(item.startTime) : '',
    endTime: item.endTime ? shortTime(item.endTime) : '',
  };
}

/** Valores → corpo do `POST`/`PUT` (razão fixa de autosserviço no servidor; o site do vínculo é preservado na edição). */
export function toAvailabilityInput(
  values: AvailabilityFormValues,
  siteId: string | null = null,
): AvailabilityInput {
  if (values.kind === 'Dated') {
    return {
      kind: 'Dated',
      weekly: null,
      date: values.date,
      startTime: values.startTime,
      endTime: values.endTime,
      siteId,
    };
  }
  const weekly: Record<string, TimeWindowInput[]> = {};
  for (const day of WEEK_DAYS) {
    const windows = values.weekly[day];
    if (windows.length > 0) {
      weekly[day] = windows.map((window) => ({ start: window.start, end: window.end }));
    }
  }
  return { kind: 'Weekly', weekly, date: null, startTime: null, endTime: null, siteId };
}

/** Dias com faixas, na ordem canônica, para exibição de uma declaração semanal. */
export function weeklyWindows(item: Availability): { day: WeekDay; windows: string[] }[] {
  return WEEK_DAYS.flatMap((day) => {
    const windows = item.weekly?.[day] ?? [];
    return windows.length === 0
      ? []
      : [{ day, windows: windows.map((w) => `${shortTime(w.start)}–${shortTime(w.end)}`) }];
  });
}

// ---- Ausências ----

export const absenceSchema = z
  .object({
    kind: z.enum(ABSENCE_KINDS),
    startDate: z.string(),
    endDate: z.string(),
    startTime: z.string(),
    endTime: z.string(),
    blocksScheduling: z.boolean(),
    note: z.string().max(1000, 'me.forms.errors.tooLong'),
  })
  .superRefine((form, context) => {
    if (!isIsoDate(form.startDate)) {
      context.addIssue({ code: 'custom', path: ['startDate'], message: 'me.forms.errors.date' });
    }
    if (!isIsoDate(form.endDate)) {
      context.addIssue({ code: 'custom', path: ['endDate'], message: 'me.forms.errors.date' });
    } else if (isIsoDate(form.startDate) && form.endDate < form.startDate) {
      context.addIssue({
        code: 'custom',
        path: ['endDate'],
        message: 'me.forms.errors.endBeforeStart',
      });
    }
    const hasStart = form.startTime.trim() !== '';
    const hasEnd = form.endTime.trim() !== '';
    if (hasStart !== hasEnd) {
      context.addIssue({
        code: 'custom',
        path: [hasStart ? 'endTime' : 'startTime'],
        message: 'me.forms.errors.timePair',
      });
    } else if (hasStart && hasEnd) {
      if (!isTime(form.startTime)) {
        context.addIssue({ code: 'custom', path: ['startTime'], message: 'me.forms.errors.time' });
      }
      if (!isTime(form.endTime)) {
        context.addIssue({ code: 'custom', path: ['endTime'], message: 'me.forms.errors.time' });
      } else if (isTime(form.startTime) && minutesOf(form.startTime) >= minutesOf(form.endTime)) {
        context.addIssue({
          code: 'custom',
          path: ['endTime'],
          message: 'me.forms.errors.startBeforeEnd',
        });
      }
    }
  });

export type AbsenceFormValues = z.infer<typeof absenceSchema>;

export function emptyAbsenceForm(): AbsenceFormValues {
  return {
    kind: 'Vacation',
    startDate: '',
    endDate: '',
    startTime: '',
    endTime: '',
    blocksScheduling: true,
    note: '',
  };
}

export function toAbsenceInput(values: AbsenceFormValues): AbsenceInput {
  const note = values.note.trim();
  return {
    kind: values.kind,
    startDate: values.startDate,
    endDate: values.endDate,
    blocksScheduling: values.blocksScheduling,
    ...(values.startTime.trim() !== ''
      ? { startTime: values.startTime, endTime: values.endTime }
      : {}),
    ...(note !== '' ? { note } : {}),
  };
}
